import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"

type Testimonial = {
  id: number
  brand: string
  quote: string
  attribution: string
  display_order: number
  visible: boolean
  highlighted: boolean
  sample: boolean
  consent_obtained_on: string | null
  created_at: string
}

type Props = PageProps & {
  testimonials: Testimonial[]
  brand: "gisela" | "sanctuary" | null
}

export default function TestimonialsIndex() {
  const { props } = usePage<Props>()
  const { testimonials, brand } = props
  const pageTitle =
    brand === "gisela"
      ? "Testimonials — Gisela Ramirez"
      : brand === "sanctuary"
        ? "Testimonials — Sanctuary"
        : "Testimonials"
  const flash = props.flash

  const [removeId, setRemoveId] = React.useState<number | null>(null)
  const [removeAttribution, setRemoveAttribution] = React.useState("")

  function handleRemove() {
    if (!removeId) return
    router.delete(`/admin/testimonials/${removeId}`)
    setRemoveId(null)
  }

  return (
    <AdminShell>
      <Head title={pageTitle}>
        <meta name="description" content="Manage client testimonials for Sanctuary Therapy Centre." />
        <meta property="og:title" content="Testimonials" />
        <meta property="og:description" content="Manage client testimonials for Sanctuary Therapy Centre." />
      </Head>

      <PageHeader
        title={pageTitle}
        description="Client quotes shown on the therapy page. Drag display_order to control sequence."
        actions={
          <Button asChild>
            <Link href="/admin/testimonials/new">+ Add Testimonial</Link>
          </Button>
        }
      />

      {flash?.notice && (
        <p className="mt-4 text-sm text-accent">{flash.notice}</p>
      )}

      <div className="mt-6 overflow-hidden rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead className="border-b border-hairline bg-surface">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Quote</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted sm:table-cell">Attribution</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Visible</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Order</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {testimonials.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink-muted">
                  No testimonials yet.
                </td>
              </tr>
            )}
            {testimonials.map((t) => (
              <tr key={t.id} className="bg-white hover:bg-surface/30 transition-colors">
                <td className="max-w-xs px-4 py-3 text-ink-body">
                  <p className="truncate">{t.quote.slice(0, 80)}{t.quote.length > 80 ? "…" : ""}</p>
                </td>
                <td className="hidden px-4 py-3 text-ink-muted sm:table-cell">
                  {t.attribution}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <Badge tone={t.visible ? "accent" : "neutral"}>
                      {t.visible ? "Visible" : "Hidden"}
                    </Badge>
                    {t.highlighted && <Badge tone="signal">On About Me</Badge>}
                    {t.sample && <Badge tone="solid">Sample — not a real client</Badge>}
                    {!t.sample && !t.consent_obtained_on && <Badge tone="signal">No consent recorded</Badge>}
                  </div>
                </td>
                <td className="hidden px-4 py-3 text-ink-muted lg:table-cell">
                  {t.display_order}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button asChild variant="secondary" size="sm">
                      <Link href={`/admin/testimonials/${t.id}/edit`}>Edit</Link>
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-danger-display hover:border-danger hover:bg-danger-faded"
                      onClick={() => { setRemoveId(t.id); setRemoveAttribution(t.attribution) }}
                    >
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={removeId !== null} onOpenChange={(open) => !open && setRemoveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove testimonial?</DialogTitle>
            <DialogDescription>
              This will permanently remove the testimonial from <strong>{removeAttribution}</strong> and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRemoveId(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRemove}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  )
}
